import React, { createRef, memo, useState } from 'react';
import { Header } from '../../components/layout/header';
import Search from 'antd/es/input/Search';
import { Button, Dropdown, message, Modal } from 'antd';
import { useDebounce } from '../../hook/use-debounce';
import { OptionApi } from '../../apis';
import { Table, TableRef } from '../../components/table/table';
import { ExclamationCircleOutlined, FileAddOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { BrandFormRef } from '../../components/model/form';
import { OptionForm } from '../../components/option/form';
import { FileUpload } from '../../components/upload/file-upload';
import { PermissionCode, useRole } from '../../hook/use-role';

const PAGE_NAME = PermissionCode.OPTION;
const OPTION_UPLOAD = PermissionCode.OPTION_UPLOAD;

const OptionListScreen = memo(() => {
  const [value, setValue] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [action, setAction] = useState<any>();

  const [messageApi, contextHolderMessage] = message.useMessage();

  const formRef = createRef<BrandFormRef | null>();
  const tableRef = createRef<TableRef>();

  const [modal, contextHolder] = Modal.useModal();

  const query = useDebounce(value, 300);

  const { check } = useRole();

  const column = columns({
    options: {
      isDelete: check.isDelete(PAGE_NAME),
      isUpdate: check.isEdit(PAGE_NAME),
    },
    setType: async (type: 'delete' | 'update', id: string) => {
      console.log('type :', tableRef.current);

      switch (type) {
        case 'update':
          const res = await OptionApi.get(id);
          setAction(res);
          setIsModalOpen(true);
          break;
        case 'delete':
          modal.confirm({
            title: 'Устгах',
            icon: <ExclamationCircleOutlined />,
            content: 'Устгахдаа итгэлтэй байна уу!',
            okText: 'Тийм',
            cancelText: 'Үгүй',
            onOk: async () => {
              await OptionApi.remove(id);
              tableRef.current?.reload();
              messageApi.success('Амжилттай устлаа');
            },
          });
          break;
      }
    },
  });

  const onSearch = (value: any) => {
    setValue(value.target.value);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    const res = await formRef.current?.submitForm();
    if (res) {
      onSubmit(res);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setAction(undefined);
  };

  const onSubmit = async (values: any) => {
    try {
      if (action) {
        await OptionApi.update({
          id: action.id,
          ...values,
          values: values.values.map((item: any) => {
            return {
              name: item.name,
            };
          }),
        });

        setIsModalOpen(false);
        setAction(undefined);
        tableRef.current?.reload();

        messageApi.success('Амжилттай засварлалаа');
      } else {
        await OptionApi.create(values);

        setIsModalOpen(false);

        tableRef.current?.reload();
        messageApi.success('Амжилттай');
      }
    } catch (err: any) {
      messageApi.error(err?.message);
    }
  };

  return (
    <>
      {contextHolderMessage}
      <div className="flex-col bg-white rounded-[8px] p-5">
        <Header title="Нэмэлт тоноглол" isGoBack={false} />
        <div className="">
          <div className="flex flex-row justify-between pb-[20px]">
            <div className="flex flex-row">
              <Search placeholder="Хайх..." onChange={onSearch} style={{ width: 300 }} />
              <div className="w-[10px]" />
              <Button type="default">Цэвэрлэх</Button>
            </div>
            <div className="flex flex-row">
              <FileUpload>
                {check.isAccess(OPTION_UPLOAD) && (
                  <Button type="default" icon={<FileAddOutlined />}>
                    Файл нэмэх
                  </Button>
                )}
              </FileUpload>
              <div className="w-[10px]" />
              {check.isCreate(PAGE_NAME) && (
                <Button type="primary" onClick={showModal} icon={<PlusCircleOutlined />}>
                  Нэмэх
                </Button>
              )}
            </div>
          </div>
          <Table ref={tableRef} columns={column} query={query} loadData={OptionApi.list} />
        </div>
      </div>

      {isModalOpen && (
        <Modal
          title="Нэмэлт тоноглол"
          closable={{ 'aria-label': 'Custom Close Button' }}
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          okText="Нэмэх"
          cancelText="Буцах"
          maskClosable={false}
        >
          <OptionForm action={action} ref={formRef} onSubmit={onSubmit} />
        </Modal>
      )}

      {contextHolder}
    </>
  );
});

const columns: any = ({
  setType,
  options,
}: {
  setType: (type: 'delete' | 'update', id: string) => void;
  options: {
    isDelete: boolean;
    isUpdate: boolean;
  };
}) => [
  {
    title: '#',
    key: 'rowIndex',
    width: 70,
    align: 'center',
    render: (_: any, __: any, index: any) => index + 1,
  },
  {
    title: 'Брэнд',
    key: 'brand',
    render: (item: any, __: any, index: any) => item?.carBrand?.name,
  },
  {
    title: 'Загвар',
    key: 'model',
    render: (item: any, __: any, index: any) => item?.carModel?.name,
  },
  {
    title: 'Нэр',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Зэрэглэл',
    key: 'level',
    render: (item: any, __: any, index: any) => item?.level?.name,
  },
  {
    title: 'Хувь',
    dataIndex: 'extraPercent',
    key: 'extraPercent',
    width: 150,
  },
  {
    key: 'rowIndex',
    width: 150,
    align: 'center',
    render: (item: any, _: any, index: any) => {
      if (!options?.isDelete && !options?.isUpdate) {
        return <></>;
      }

      let menus = [];

      if (options?.isUpdate === true) {
        menus.push({
          key: 'update',
          label: <span>Засах</span>,
          onClick: () => {
            console.log('item :', item);
            setType('update', item?.id!);
          },
        });
      }

      if (options?.isDelete === true) {
        menus.push({
          key: 'delete',
          label: <span className="text-red-500">Устгах</span>,
          onClick: () => setType('delete', item?.id!),
        });
      }

      return (
        <Dropdown
          trigger={['click']}
          menu={{
            items: menus,
          }}
          arrow
          placement="bottomLeft"
        >
          <Button type="default">Үйлдэл</Button>
        </Dropdown>
      );
    },
  },
];

OptionListScreen.displayName = 'OptionListScreen';

export { OptionListScreen };
